import type { AssistantUsageText } from "@/locales/assistant-usage";
import type { MeterLevel, UsageMeterDto } from "@/types/ai-assistant-usage";

/** Logika murni tampilan meter pemakaian AI (format angka, warna, peringatan). */

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3_600;
const SECONDS_PER_DAY = 86_400;
const USD_DECIMALS = 2;
const USD_SMALL_DECIMALS = 4;
const USD_SMALL_THRESHOLD = 0.01;

export const LEVEL_COLOR: Record<MeterLevel, string> = {
	monitor: "blue",
	ok: "teal",
	warn: "yellow",
	full: "red",
};

export function formatNumber(n: number, lang: "id" | "en" = "id"): string {
	return Math.round(n).toLocaleString(lang === "en" ? "en-US" : "id-ID");
}

/** Detik → menit (1 desimal bila tidak bulat) untuk tampilan. */
export function formatMinutes(
	seconds: number,
	lang: "id" | "en" = "id",
): string {
	const minutes = seconds / SECONDS_PER_MINUTE;
	return minutes.toLocaleString(lang === "en" ? "en-US" : "id-ID", {
		maximumFractionDigits: 1,
	});
}

export function formatUsd(usd: number, lang: "id" | "en" = "id"): string {
	const digits =
		usd > 0 && usd < USD_SMALL_THRESHOLD ? USD_SMALL_DECIMALS : USD_DECIMALS;
	return usd.toLocaleString(lang === "en" ? "en-US" : "id-ID", {
		minimumFractionDigits: digits,
		maximumFractionDigits: digits,
	});
}

/** "3 jam 12 menit" / "2 hari 4 jam" dari sisa detik. */
export function formatDuration(
	seconds: number,
	lang: "id" | "en" = "id",
): string {
	const u =
		lang === "en"
			? { d: "d", h: "h", m: "min" }
			: { d: "hari", h: "jam", m: "menit" };
	const days = Math.floor(seconds / SECONDS_PER_DAY);
	const hours = Math.floor((seconds % SECONDS_PER_DAY) / SECONDS_PER_HOUR);
	const minutes = Math.floor((seconds % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
	if (days > 0) return `${days} ${u.d} ${hours} ${u.h}`;
	if (hours > 0) return `${hours} ${u.h} ${minutes} ${u.m}`;
	return `${Math.max(minutes, 1)} ${u.m}`;
}

/** Nilai meter dalam satuan tampilan (token atau menit). */
export function meterAmount(
	m: UsageMeterDto,
	value: number,
	lang: "id" | "en",
): string {
	return m.unit === "seconds"
		? formatMinutes(value, lang)
		: formatNumber(value, lang);
}

export function meterTitle(m: UsageMeterDto, t: AssistantUsageText): string {
	const kind = m.kind === "chat" ? t.chat : t.voice;
	return `${kind} · ${m.period === "day" ? t.today : t.month}`;
}

/** Meter yang sudah mencapai ambang peringatan (warn/full) — untuk banner admin. */
export function warningMeters(meters: UsageMeterDto[]): UsageMeterDto[] {
	return meters.filter((m) => m.level === "warn" || m.level === "full");
}
